import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCampaign, updateCampaign } from '../lib/api';
import { Campaign } from '@shared/types';
import QRModal from '../components/QRModal';
import { useTheme } from '../lib/useTheme';

export default function CampaignPage() {
  const { slug } = useParams<{ slug: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [characters, setCharacters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // WHY: Inline-edit state for name/level — kept separate so either field can
  // be edited independently without disturbing the other.
  const [editingName, setEditingName] = useState(false);
  const [editingLevel, setEditingLevel] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftLevel, setDraftLevel] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);

  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (!slug) return;
    getCampaign(slug)
      .then((data) => {
        setCampaign(data.campaign);
        setCharacters(data.characters);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <div className="text-center py-8 text-dnd-fg">Loading...</div>;
  if (error) return <div className="text-center py-8 text-dnd-danger-text">{error}</div>;
  if (!campaign) return <div className="text-center py-8 text-dnd-fg">Campaign not found</div>;

  const startEditName = () => {
    setDraftName(campaign.name);
    setFieldError(null);
    setEditingName(true);
  };
  const startEditLevel = () => {
    setDraftLevel(String(campaign.level));
    setFieldError(null);
    setEditingLevel(true);
  };
  const cancelName = () => { setEditingName(false); setFieldError(null); };
  const cancelLevel = () => { setEditingLevel(false); setFieldError(null); };

  const saveName = async () => {
    if (!slug) return;
    setSaving(true);
    setFieldError(null);
    try {
      const updated = await updateCampaign(slug, { name: draftName });
      setCampaign((prev) => (prev ? { ...prev, ...updated } : prev));
      setEditingName(false);
    } catch (err: any) {
      setFieldError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const saveLevel = async () => {
    if (!slug) return;
    setSaving(true);
    setFieldError(null);
    try {
      const updated = await updateCampaign(slug, { level: Number(draftLevel) });
      setCampaign((prev) => (prev ? { ...prev, ...updated } : prev));
      setEditingLevel(false);
    } catch (err: any) {
      setFieldError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="card mb-6 flex-1">
          {editingName ? (
            <div className="flex flex-col gap-2">
              <label className="label" htmlFor="campaign-name-input">Campaign Name</label>
              <input
                id="campaign-name-input"
                className="input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') cancelName();
                }}
                autoFocus
                disabled={saving}
              />
              <div className="flex gap-2">
                <button type="button" onClick={saveName} disabled={saving} className="btn btn-gold">Save</button>
                <button type="button" onClick={cancelName} disabled={saving} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          ) : (
            // WHY: Click the name itself to edit (no separate Edit button) —
            // role/tabIndex/keyboard keep it operable for screen readers & keyboard users.
            <h2
              role="button"
              tabIndex={0}
              onClick={startEditName}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startEditName();
                }
              }}
              aria-label="Edit campaign name"
              className="heading text-3xl font-bold cursor-pointer hover:text-dnd-accent transition-colors"
            >
              {campaign.name}
            </h2>
          )}

          {editingLevel ? (
            <div className="flex flex-col gap-2 mt-4">
              <label className="label" htmlFor="campaign-level-input">Level</label>
              <input
                id="campaign-level-input"
                type="number"
                min={1}
                max={20}
                className="input w-32"
                value={draftLevel}
                onChange={(e) => setDraftLevel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveLevel();
                  if (e.key === 'Escape') cancelLevel();
                }}
                autoFocus
                disabled={saving}
              />
              <div className="flex gap-2">
                <button type="button" onClick={saveLevel} disabled={saving} className="btn btn-gold">Save</button>
                <button type="button" onClick={cancelLevel} disabled={saving} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          ) : (
            // WHY: Click the level label to edit; the character count stays plain text.
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <button
                type="button"
                onClick={startEditLevel}
                aria-label="Edit campaign level"
                className="text-dnd-muted hover:text-dnd-accent transition-colors cursor-pointer font-semibold min-h-[44px]"
              >
                Level {campaign.level}
              </button>
              <span className="text-dnd-muted">&bull; {characters.length} characters</span>
            </div>
          )}

          {fieldError && <p className="text-dnd-danger-text mt-2">{fieldError}</p>}
        </div>

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-pressed={theme === 'dark'}
          className="btn tab-btn px-3 shrink-0"
        >
          {theme === 'dark' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Link
          to={`/${slug}/party`}
          className="btn btn-gold w-full text-center"
        >
          Party Stats
        </Link>
        <Link
          to={`/${slug}/character/new`}
          className="btn tab-btn w-full text-right"
        >
          Add PC
        </Link>
      </div>

      <div className="card">
        <h3 className="text-xl font-semibold mb-4 text-dnd-fg">Characters</h3>
        {characters.length === 0 ? (
          <p className="text-dnd-muted">No characters yet. Be the first to join!</p>
        ) : (
          <div className="grid gap-4">
            {characters.map((char) => (
              <Link
                key={char.id}
                to={`/${slug}/character/${char.id}`}
                className="block p-4 panel hover:border-dnd-accent transition-colors"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-semibold text-dnd-fg">{char.name}</span>
                    <span className="ml-2 text-dnd-muted capitalize">({char.class})</span>
                  </div>
                  {char.excluded ? (
                    <span className="text-dnd-accent-hover text-sm font-semibold">Excluded</span>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => setShowQR(true)}
          className="btn btn-secondary w-full sm:w-auto text-center"
        >
          Share QR
        </button>
      </div>

      <QRModal
        isOpen={showQR}
        onClose={() => setShowQR(false)}
        url={window.location.origin + '/' + campaign.slug}
      />
    </div>
  );
}
